/**
 * EmergencyWeatherBanner · v2.7.65+E213
 *
 * A proactive, high-signal severe-weather banner shown at the very top of the
 * admin console for EVERY logged-in admin/dispatch user. It polls
 * `/api/admin/weather/alerts/emergency` (push/warning events, Severe/Extreme
 * severity, or heat/fire events) every 60s and flashes the most urgent active
 * alert. Dismissible per-alert; a brand-new alert id re-shows automatically so
 * a fresh danger is never hidden.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { AlertTriangle, CloudLightning, Flame, ThermometerSun, X } from "lucide-react";
import adminApi from "@/lib/adminApi";

const DISMISS_KEY = "rtg_wx_dismissed_v1";
const POLL_MS = 60000;

function loadDismissed() {
  try {
    const raw = JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}
function saveDismissed(ids) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function alertTone(a) {
  const ev = (a.event || "").toLowerCase();
  if (/fire|red flag/.test(ev)) return { bg: "from-orange-600 to-red-600", Icon: Flame };
  if (/heat/.test(ev)) return { bg: "from-amber-500 to-orange-600", Icon: ThermometerSun };
  if (/tornado|thunderstorm|lightning/.test(ev)) return { bg: "from-red-600 to-red-700", Icon: CloudLightning };
  return { bg: "from-red-600 to-rose-700", Icon: AlertTriangle };
}

function untilLabel(expires) {
  if (!expires) return null;
  try {
    const d = new Date(expires);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return null;
  }
}

export default function EmergencyWeatherBanner() {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(loadDismissed);
  const [mounted, setMounted] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/weather/alerts/emergency");
      // v2.7.65+E243 · Fix 1 · Heat Advisory is fully hidden from the Admin
      // Console — filtered here so it never appears or pops up. Other alerts
      // (incl. Excessive Heat Warnings, fire, tornado) are untouched.
      const rows = ((data && data.alerts) || []).filter(
        (a) => !/heat\s*advisory/i.test(a.event || "")
      );
      setAlerts(rows);
      // Prune dismissed ids that are no longer active so the store stays small
      // (and a re-issued id would re-show).
      setDismissed((prev) => {
        const activeIds = new Set(rows.map((r) => r.id).filter(Boolean));
        const next = prev.filter((id) => activeIds.has(id));
        if (next.length !== prev.length) saveDismissed(next);
        return next;
      });
    } catch {
      /* silent — banner just stays hidden if the feed is unavailable */
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const t = setInterval(fetchAlerts, POLL_MS);
    return () => clearInterval(t);
  }, [fetchAlerts]);

  const visible = useMemo(
    () => alerts.filter((a) => a.id && !dismissed.includes(a.id)),
    [alerts, dismissed]
  );
  const current = visible[0] || null;

  useEffect(() => {
    if (current) {
      // trigger slide-in on the next frame
      const r = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(r);
    }
    setMounted(false);
  }, [current]);

  const dismiss = useCallback(() => {
    if (!current) return;
    setDismissed((prev) => {
      const next = prev.includes(current.id) ? prev : [...prev, current.id];
      saveDismissed(next);
      return next;
    });
  }, [current]);

  if (!current) return null;

  const { bg, Icon } = alertTone(current);
  const until = untilLabel(current.expires);
  const extra = visible.length - 1;

  return (
    <div
      role="alert"
      data-testid="emergency-weather-banner"
      data-alert-id={current.id}
      className={`fixed top-0 inset-x-0 z-[200] bg-gradient-to-r ${bg} text-white shadow-xl border-b border-black/20
        transition-transform duration-300 ease-out ${mounted ? "translate-y-0" : "-translate-y-full"}`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center gap-3 px-4 py-2">
        <span className="relative flex h-6 w-6 flex-shrink-0 items-center justify-center">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white/30 animate-ping" />
          <Icon className="relative h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-[10px] font-black uppercase tracking-[0.15em] bg-black/25 px-1.5 py-0.5 rounded">
              {current.severity || "Alert"}
            </span>
            <span className="font-bold text-sm truncate" data-testid="emergency-weather-event">
              {current.event || "Weather Alert"}
            </span>
            {current.areaDesc && (
              <span className="text-xs text-white/85 truncate hidden sm:inline">· {current.areaDesc}</span>
            )}
            {until && (
              <span className="text-[11px] text-white/80 whitespace-nowrap">· until {until}</span>
            )}
            {extra > 0 && (
              <span
                className="text-[10px] font-bold bg-white/20 px-1.5 py-0.5 rounded whitespace-nowrap"
                data-testid="emergency-weather-more"
              >
                +{extra} more active
              </span>
            )}
          </div>
          {current.headline && (
            <div className="text-[11px] text-white/85 truncate hidden md:block">{current.headline}</div>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          data-testid="emergency-weather-dismiss"
          aria-label="Dismiss weather alert"
          className="flex-shrink-0 p-1.5 rounded-md hover:bg-black/25 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
